import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import KwhCell from '../../components/KwhCell'
import MoneyCell from '../../components/MoneyCell'
import PercentBarCell from '../../components/PercentBarCell'
import StatusBadge from '../../components/StatusBadge'
import type { PropertyCompletenessRow } from '../../saber.types'
import { columnHelp } from '../../columnHelp'
import { tableBodyRowClass } from '../../tableRowStyles'
import { completenessBarClass, completenessPctClass, completenessPctRaw, completenessStatusFromPct } from './completeness'
import { completenessStatusTooltip, displayTenantName } from './labels'
import { CELL_COLOR } from '../../cellColor'
import NullDash from '../../components/NullDash'
import { GroupChevron, GroupViewToolbar, GROUP_SUB_ROW_CLASS, SortableTh, nextSort, sortRows } from './grouped'
import type { GroupSort, GroupView, SortValue } from './grouped'

const PAD = 'px-3 py-2'

interface CompletenessNumbers {
  allocatedKwh: number
  billedKwh: number
  unbilledKwh: number
  unbilledValue: number
}

interface CompletenessGroup extends CompletenessNumbers {
  groupKey: string
  tenantName: string | null
  meters: PropertyCompletenessRow[]
  completenessPct: number | null
  status: PropertyCompletenessRow['status']
}

const tenantGroupKey = (row: PropertyCompletenessRow): string =>
  row.tenantName ?? `__meter__:${row.meterServiceCuid}`

const aggregateNumbers = (meters: PropertyCompletenessRow[]): CompletenessNumbers =>
  meters.reduce<CompletenessNumbers>(
    (acc, row) => ({
      allocatedKwh: acc.allocatedKwh + row.allocatedKwh,
      billedKwh: acc.billedKwh + row.billedKwh,
      unbilledKwh: acc.unbilledKwh + row.unbilledKwh,
      unbilledValue: acc.unbilledValue + row.unbilledValue
    }),
    { allocatedKwh: 0, billedKwh: 0, unbilledKwh: 0, unbilledValue: 0 }
  )

const groupCompleteness = (rows: PropertyCompletenessRow[]): CompletenessGroup[] => {
  const byKey = new Map<string, CompletenessGroup>()
  const out: CompletenessGroup[] = []
  for (const row of rows) {
    const groupKey = tenantGroupKey(row)
    let group = byKey.get(groupKey)
    if (group == null) {
      group = {
        groupKey,
        tenantName: row.tenantName,
        meters: [],
        allocatedKwh: 0,
        billedKwh: 0,
        unbilledKwh: 0,
        unbilledValue: 0,
        completenessPct: null,
        status: 'OK'
      }
      byKey.set(groupKey, group)
      out.push(group)
    }
    group.meters.push(row)
  }
  for (const group of out) {
    const totals = aggregateNumbers(group.meters)
    group.allocatedKwh = totals.allocatedKwh
    group.billedKwh = totals.billedKwh
    group.unbilledKwh = totals.unbilledKwh
    group.unbilledValue = totals.unbilledValue
    group.completenessPct = completenessPctRaw(totals)
    group.status = completenessStatusFromPct(group.completenessPct)
  }
  return out
}

interface ColumnDef {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  groupValue: (g: CompletenessGroup) => SortValue
  meterValue: (r: PropertyCompletenessRow) => SortValue
}

const COLUMNS: ColumnDef[] = [
  { key: 'tenant', label: 'Tenant', groupValue: (g) => g.tenantName, meterValue: (r) => r.tenantName },
  { key: 'meter', label: 'Meter', groupValue: (g) => g.meters.length, meterValue: (r) => r.meterNumber },
  {
    key: 'alloc',
    label: 'Shadow kWh',
    align: 'right',
    groupValue: (g) => g.allocatedKwh,
    meterValue: (r) => r.allocatedKwh
  },
  { key: 'billed', label: 'Billed kWh', align: 'right', groupValue: (g) => g.billedKwh, meterValue: (r) => r.billedKwh },
  {
    key: 'unbilled',
    label: 'Unbilled kWh',
    align: 'right',
    groupValue: (g) => g.unbilledKwh,
    meterValue: (r) => r.unbilledKwh
  },
  {
    key: 'unbilledValue',
    label: 'Unbilled ($)',
    align: 'right',
    groupValue: (g) => g.unbilledValue,
    meterValue: (r) => r.unbilledValue
  },
  {
    key: 'pct',
    label: 'Completeness %',
    align: 'right',
    groupValue: (g) => g.completenessPct,
    meterValue: (r) => completenessPctRaw(r)
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    groupValue: (g) => g.status,
    meterValue: (r) => r.status
  }
]

const tenantCell = (name: string | null, propertyName?: string | null): React.ReactNode => {
  if (name == null) return <NullDash />
  const short = displayTenantName(name, propertyName)
  return short !== name ? <span title={name}>{short}</span> : short
}

const pctCell = (pct: number | null): React.ReactNode => {
  if (pct == null) return <NullDash />
  return (
    <PercentBarCell
      value={pct}
      valueClassName={classnames('font-medium', completenessPctClass(pct))}
      barClassName={completenessBarClass(pct)}
      ariaDescription="completeness"
    />
  )
}

const numericCells = (x: CompletenessNumbers & { completenessPct: number | null; status: PropertyCompletenessRow['status'] }, bold = false): React.ReactNode => (
  <>
    <td className={classnames(PAD, 'text-right text-slate-700 dark:text-slate-200', bold && 'font-medium')}>
      <KwhCell value={x.allocatedKwh} />
    </td>
    <td className={classnames(PAD, 'text-right text-slate-700 dark:text-slate-200', bold && 'font-medium')}>
      <KwhCell value={x.billedKwh} />
    </td>
    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted, bold && 'font-medium')}>
      <KwhCell
        value={x.unbilledKwh}
        role="loss"
      />
    </td>
    <td className={classnames(PAD, 'text-right', bold && 'font-medium')}>
      <MoneyCell value={x.unbilledValue} role="loss" />
    </td>
    <td className={classnames(PAD, 'text-right', bold && 'font-medium')}>{pctCell(x.completenessPct)}</td>
    <td className={classnames(PAD, 'text-center')}>
      <StatusBadge status={x.status} title={completenessStatusTooltip(x.status)} />
    </td>
  </>
)

export interface CompletenessTableProps {
  rows: PropertyCompletenessRow[]
  propertyName?: string | null
  actions?: React.ReactNode
}

export const CompletenessTable: React.FC<CompletenessTableProps> = ({ rows, propertyName, actions }) => {
  const [view, setView] = useState<GroupView>('tenant')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [sort, setSort] = useState<GroupSort>({ key: null, direction: 'asc' })

  const groups = useMemo(() => groupCompleteness(rows), [rows])
  const multiKeys = useMemo(() => groups.filter((g) => g.meters.length > 1).map((g) => g.groupKey), [groups])
  const allOpen = multiKeys.length > 0 && multiKeys.every((k) => expanded.has(k))

  const toggle = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const sortCol = COLUMNS.find((c) => c.key === sort.key)
  const sortedGroups = useMemo(() => sortRows(groups, sort, sortCol?.groupValue), [groups, sort, sortCol])
  const sortedFlat = useMemo(() => sortRows(rows, sort, sortCol?.meterValue), [rows, sort, sortCol])

  const totals = useMemo(() => {
    const numbers = aggregateNumbers(rows)
    const completenessPct = completenessPctRaw(numbers)
    return {
      ...numbers,
      completenessPct,
      status: completenessStatusFromPct(completenessPct)
    }
  }, [rows])

  return (
    <div>
      <GroupViewToolbar
        view={view}
        onViewChange={setView}
        canExpand={multiKeys.length > 0}
        allOpen={allOpen}
        onToggleAll={() => setExpanded(allOpen ? new Set() : new Set(multiKeys))}
        ariaLabel="Completeness table view"
        actions={actions}
      />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  align={c.align}
                  active={sort.key === c.key}
                  direction={sort.direction}
                  onClick={() => setSort((prev) => nextSort(prev, c.key))}
                  title={columnHelp('completeness', c.key)}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-400 dark:text-slate-500">
                  No meters.
                </td>
              </tr>
            ) : view === 'flat' ? (
              sortedFlat.map((r, index) => (
                <tr key={r.meterServiceCuid} className={tableBodyRowClass(index)}>
                  <td className={classnames(PAD, 'text-slate-700 dark:text-slate-200')}>
                    {tenantCell(r.tenantName, propertyName)}
                  </td>
                  <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>{r.meterNumber}</td>
                  {numericCells({
                    allocatedKwh: r.allocatedKwh,
                    billedKwh: r.billedKwh,
                    unbilledKwh: r.unbilledKwh,
                    unbilledValue: r.unbilledValue,
                    completenessPct: completenessPctRaw(r),
                    status: r.status
                  })}
                </tr>
              ))
            ) : (
              (() => {
                let rowIndex = 0
                return sortedGroups.flatMap((g) => {
                const multi = g.meters.length > 1
                const open = expanded.has(g.groupKey)
                const groupIndex = rowIndex++
                const out = [
                  <tr
                    key={g.groupKey}
                    onClick={multi ? () => toggle(g.groupKey) : undefined}
                    className={tableBodyRowClass(groupIndex, multi ? 'cursor-pointer' : undefined)}
                  >
                    <td className={PAD}>
                      <span className="inline-flex items-center gap-1.5">
                        {multi ? <GroupChevron open={open} /> : <span className="inline-block w-3" />}
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {tenantCell(g.tenantName, propertyName)}
                        </span>
                      </span>
                    </td>
                    <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>
                      {multi ? (
                        <span className="font-sans text-slate-400 dark:text-slate-500">
                          {open ? '' : `${g.meters.length} meters`}
                        </span>
                      ) : (
                        g.meters[0].meterNumber
                      )}
                    </td>
                    {numericCells(g, multi)}
                  </tr>
                ]
                if (multi && open) {
                  for (const m of g.meters) {
                    out.push(
                      <tr key={`${g.groupKey}::${m.meterServiceCuid}`} className={GROUP_SUB_ROW_CLASS}>
                        <td className={classnames(PAD, 'pl-10')}>
                          <span className="text-xs text-slate-400 dark:text-slate-500">└</span>
                        </td>
                        <td className={classnames(PAD, 'font-mono text-xs text-slate-600 dark:text-slate-300')}>
                          {m.meterNumber}
                        </td>
                        {numericCells({
                          allocatedKwh: m.allocatedKwh,
                          billedKwh: m.billedKwh,
                          unbilledKwh: m.unbilledKwh,
                          unbilledValue: m.unbilledValue,
                          completenessPct: completenessPctRaw(m),
                          status: m.status
                        })}
                      </tr>
                    )
                  }
                }
                return out
              })
              })()
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-semibold text-slate-900 dark:border-slate-600 dark:text-slate-100">
                <td className={PAD}>TOTAL</td>
                <td className={PAD} />
                {numericCells(totals, true)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

export default CompletenessTable
